/**
 * Creates a JWT token with the provided payload
 * @param payload - The payload to include in the JWT
 * @returns A JWT token string
 */
export function createJWT(payload: Record<string, unknown>): string {
  // Note: In a production environment, you should sign the token with a secret key
  // This is a simplified version that doesn't include a signature
  const header = {
    alg: 'none',
    typ: 'JWT'
  };

  // Simple and reliable JWT encoding
  try {
    // For Unicode characters, use encodeURIComponent + btoa
    const headerJson = JSON.stringify(header);
    const payloadJson = JSON.stringify(payload);
    
    const encodedHeader = btoa(encodeURIComponent(headerJson).replace(/%([0-9A-F]{2})/g, (_, p1) => String.fromCharCode(parseInt(p1, 16)))).replace(/=+$/, '');
    const encodedPayload = btoa(encodeURIComponent(payloadJson).replace(/%([0-9A-F]{2})/g, (_, p1) => String.fromCharCode(parseInt(p1, 16)))).replace(/=+$/, '');
    
    return `${encodedHeader}.${encodedPayload}.`;
  } catch (error) {
    console.error('JWT encoding failed, using simple fallback:', error);
    // Simple ASCII fallback
    const encodedHeader = btoa(JSON.stringify(header)).replace(/=+$/, '');
    const encodedPayload = btoa(JSON.stringify(payload)).replace(/=+$/, '');
    return `${encodedHeader}.${encodedPayload}.`;
  }
}

/**
 * Decodes a JWT token without verification
 * @param token - The JWT token to decode
 * @returns The decoded token payload or null if invalid
 */
export function decodeJWT<T = unknown>(token: string): T | null {
  if (!token) return null;
  
  try {
    // Split the token into its parts
    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid JWT format');
    }
    
    // Decode the payload (second part)
    const payload = parts[1];
    
    try {
      // Try decodeURIComponent approach first (matches encoding)
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      const decodedJson = decodeURIComponent(decoded.split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
      return JSON.parse(decodedJson) as T;
    } catch (unicodeError) {
      // Fallback to simple ASCII decoding
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(decoded) as T;
    }
  } catch (error) {
    console.error('Failed to decode JWT:', error);
    return null;
  }
}

/**
 * Type guard to check if an object has a specific property
 */
function hasProperty<T extends object, K extends string>(
  obj: T,
  prop: K
): obj is T & Record<K, unknown> {
  return prop in obj;
}

/**
 * Safely gets a value from a JWT payload
 * @param token - The JWT token
 * @param key - The key to get from the payload
 * @returns The value or undefined if not found
 */
export function getJWTValue<T = unknown>(
  token: string,
  key: string
): T | undefined {
  const payload = decodeJWT<Record<string, unknown>>(token);
  return payload && hasProperty(payload, key) ? (payload[key] as T) : undefined;
}
